import React from 'react';
import { Dices, Gamepad2, Monitor, Shield, Swords, Users } from 'lucide-react';
import './SetupScreen.css';

export type SetupPlayers = '2p' | '4p';
export type SetupDifficulty = 'EASY' | 'NORMAL' | 'HARD';
export type SetupMode = 'LOCAL' | 'ONLINE';

interface SetupScreenProps {
  players: SetupPlayers;
  difficulty: SetupDifficulty;
  mode: SetupMode;
  vsComputer: boolean;
  teamMode: boolean;
  onPlayersChange: (value: SetupPlayers) => void;
  onDifficultyChange: (value: SetupDifficulty) => void;
  onModeChange: (value: SetupMode) => void;
  onVsComputerChange: (value: boolean) => void;
  onTeamModeChange: (value: boolean) => void;
  onStart: () => void;
  onBack?: () => void;
}

export default function SetupScreen(props: SetupScreenProps): JSX.Element {
  const {
    players,
    difficulty,
    mode,
    vsComputer,
    teamMode,
    onPlayersChange,
    onDifficultyChange,
    onModeChange,
    onVsComputerChange,
    onTeamModeChange,
    onStart,
  } = props;

  const showOpponent = mode === 'LOCAL' && players === '2p';

  return (
    <div className="setup-shell">
      <div className="setup-panel">
        <div className="setup-topbar">
          <span className="setup-topbar-spacer" />

          <div className="setup-topbar-center">
            <div className="setup-brandline">
              <div className="setup-logo" aria-label="Omulongo">
                <Dices size={18} aria-hidden="true" />
                <span>omu</span>
                <span className="diamond">1♦</span>
                <span>ngo</span>
              </div>
            </div>
            <p className="setup-sub">Configure your table before starting.</p>
          </div>

          <span className="setup-topbar-spacer" />
        </div>

        <div className="setup-body">
          <div className="setup-main" role="region" aria-label="Setup options">
            <section className="setup-group">
            <div className="setup-label">Players</div>
            <div className="setup-segment" role="group" aria-label="Players">
              <button
                className={`setup-segment-btn variant-cyan ${players === '2p' ? 'active' : ''}`}
                onClick={() => onPlayersChange('2p')}
                type="button"
              >
                <Swords size={16} aria-hidden="true" />
                <span>2 Players</span>
              </button>
              <button
                className={`setup-segment-btn variant-violet ${players === '4p' ? 'active' : ''}`}
                onClick={() => onPlayersChange('4p')}
                type="button"
              >
                <Users size={16} aria-hidden="true" />
                <span>4 Players</span>
              </button>
            </div>
          </section>

          <section className="setup-group">
            <div className="setup-label">Difficulty</div>
            <div className="setup-segment" role="group" aria-label="Difficulty">
              <button
                className={`setup-segment-btn variant-green ${difficulty === 'EASY' ? 'active' : ''}`}
                onClick={() => onDifficultyChange('EASY')}
                type="button"
              >
                <Shield size={16} aria-hidden="true" />
                <span>Easy</span>
              </button>
              <button
                className={`setup-segment-btn variant-cyan ${difficulty === 'NORMAL' ? 'active' : ''}`}
                onClick={() => onDifficultyChange('NORMAL')}
                type="button"
              >
                <Gamepad2 size={16} aria-hidden="true" />
                <span>Normal</span>
              </button>
              <button
                className={`setup-segment-btn variant-red ${difficulty === 'HARD' ? 'active' : ''}`}
                onClick={() => onDifficultyChange('HARD')}
                type="button"
              >
                <Swords size={16} aria-hidden="true" />
                <span>Hard</span>
              </button>
            </div>
          </section>

          <section className="setup-group setup-wide">
            <div className="setup-label">Game Mode</div>
            <div className="setup-segment" role="group" aria-label="Game mode">
              <button
                className={`setup-segment-btn variant-cyan ${mode === 'LOCAL' ? 'active' : ''}`}
                onClick={() => onModeChange('LOCAL')}
                type="button"
              >
                <Monitor size={16} aria-hidden="true" />
                <span>Local</span>
              </button>
            </div>
          </section>

          {showOpponent ? (
            <section className="setup-group setup-wide">
              <div className="setup-label">Opponent</div>
              <label className="setup-switch">
                <input
                  id="vs-computer-switch"
                  type="checkbox"
                  checked={vsComputer}
                  onChange={(e) => onVsComputerChange(e.target.checked)}
                />
                <span className="setup-switch-ui" aria-hidden="true" />
                <span className="setup-switch-text">Play vs Computer (P2)</span>
              </label>
            </section>
          ) : null}

          <section className="setup-group setup-wide">
            <div className="setup-label">Team Mode</div>
            <label className="setup-switch">
              <input
                id="team-mode-switch"
                type="checkbox"
                checked={teamMode}
                onChange={(e) => onTeamModeChange(e.target.checked)}
              />
              <span className="setup-switch-ui" aria-hidden="true" />
              <span className="setup-switch-text">2v2 (if 4 players)</span>
            </label>
          </section>
          </div>
        </div>

        <div className="setup-footer">
          <button className="setup-btn primary" type="button" onClick={onStart}>
            Start Game
          </button>
        </div>
      </div>
    </div>
  );
}
